// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';
import myw from 'myWorld/base/core';

/**
 * @typedef unitScaleResultOptions
 * @property {number} value              value representation
 * @property {string} unit               unit representation
 * @property {UnitScale} unitScale   unit scale engine to use
 */

myw.UnitScale = myw.MywClass.extend({
    /**
     * @class Class to work with units.
     * @param  {object} unitDef  unit definition
     * @constructs
     * @extends {MywClass}
     */
    initialize(unitDef) {
        this.unitDef = unitDef;
        this.unitCheckRegexp = /[a-zA-Z]+$/g;
    },

    /**
     * Construct a UnitValue object
     * @param {number} value     Value of the unit
     * @param {string} unit      Unit type of the unit
     * @returns {UnitValue}
     * @throws {UnitNotDefinedError} Will throw if the unit is not defined in the unit def
     */
    value(value, unit) {
        const unitExists = this.getUnit(unit);
        if (!unitExists) throw new myw.UnitNotDefinedError(unit);
        return new myw.UnitValue(value, unit, this);
    },

    /**
     * Converts a unit from one type to another
     * @param {number} value     Value of the unit
     * @param {string} fromUnit  Unit type of the value
     * @param {string} toUnit    Unit type to convert too
     * @returns {number}         Value of the converted unit
     * @throws {UnitNotDefinedError} Will throw if the from or to unit is not defined in the unit def
     */
    convert(value, fromUnit, toUnit) {
        const fromFactor = this.unitDef.units[fromUnit];
        const toUnitFactor = this.unitDef.units[toUnit];

        if (!fromFactor) throw new myw.UnitNotDefinedError(fromUnit);
        if (!toUnitFactor) throw new myw.UnitNotDefinedError(toUnit);

        return (value * fromFactor) / toUnitFactor;
    },

    /**
     * Will attempt to parse the input string and return a UnitValue object
     * @param {string} str          Input to parse
     * @param {string} defaultUnit  Default unit to use if no unit is found on the input string
     * @returns {UnitValue}
     * @throws {UnitNotDefinedError} Will throw if the from or to unit is not defined in the unit def
     */
    fromString(input, defaultUnit) {
        const inputString = input.replace(/ /g, '').replace(/\,/g, '');
        const units = this.unitDef.units;

        //check to see if a unit could be in the string
        const match = inputString.match(this.unitCheckRegexp);

        if (!match) {
            //If no units check if letters in string - if letters present throw error
            this.checkValidNumberString(inputString);
            return new myw.UnitValue(parseFloat(inputString), defaultUnit, this);
        }

        const unitKeys = Object.keys(units);

        for (const unit of unitKeys) {
            const pos = match[0] == unit;

            if (pos) {
                this.checkValidNumberString(inputString.replace(unit, ''));
                return new myw.UnitValue(parseFloat(inputString.slice(0, match.index)), unit, this);
            }
        }

        throw new myw.UnitNotDefinedError(`${match[0]}`);
    },

    /**
     * Return the unit scale value
     * @param {string} unit  Unit to find
     * @returns {number}
     */
    getUnit(unit) {
        return this.unitDef.units[unit];
    },

    /**
     * Checks if the input string is valid, if not throws an error
     * @param {string} string
     * @throws {ParseFloatError}
     */
    checkValidNumberString(string) {
        if (isNaN(Number(string))) {
            throw new myw.ParseFloatError();
        }
    }
});

/**
 * @class Class to work with units.
 * @name UnitValue
 * @param  {object} unitDef  unit definition
 * @constructor
 */
myw.UnitValue = function(value, unit, unitScale) {
    this.value = value;
    this.unit = unit;
    this.unitScale = unitScale;
};

/**
 * Return unit value as a unit
 * @param {string} toUnit  Unit to represent the value as
 * @returns {number}
 */
myw.UnitValue.prototype.valueIn = function(toUnit) {
    return this.unitScale.convert(this.value, this.unit, toUnit);
};

/**
 * Return unit value as a string
 * @param {string} [toUnit]  Unit to represent the value as defaults to internal unit
 * @returns {number}
 */
myw.UnitValue.prototype.toString = function(toUnit, options) {
    const defaultOptions = _.extend(
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: false
        },
        options
    );

    if (isNaN(this.value)) {
        throw new myw.ParseFloatError();
    }

    if (toUnit) {
        return this.valueIn(toUnit).toLocaleString(undefined, defaultOptions) + ' ' + toUnit;
    }

    return this.value.toLocaleString(undefined, defaultOptions) + ' ' + this.unit;
};

export default myw.UnitScale;
